import { Tag } from "antd";
import React, { useEffect, useState } from "react";
import { zAttrLabelMeta } from "src/common/attributes/makeAttrLabel/types/ZAttrLabelMeta";
import { ZAttrLabelTreeData } from "src/common/attributes/makeAttrLabel/types/ZAttrLabelTreeData";
import { getViewInfo, ZAttrViewInfo } from "src/common/attrView";
import { ZModeObjectRef } from "src/common/attrView/componentsDefs";
import { getSeparator } from "src/common/attrView/viewFormItems/Separator";
import { findNodeByKey } from "src/common/findNodeByKey";
import { onError } from "src/common/onError";
import { makeUrl } from "src/routes/makeUrl";
import { PageUrl } from "src/routes/PageUrl";
import { AttrTypeName } from "src/types/AttrType";
import { ZAttribute } from "src/types/ZAttribute";
import { ZUid } from "src/types/zUid";
import { classNames } from "src/common/classNames";
import { MakeLabelParams } from "../../../types/MakeLabelParams";
import { getAttrLabelTreeData } from "../../../utils/getAttrLabelTreeData";
import { SimpleText } from "../../SimpleText/SimpleText";
import { getEntityLabel } from "../getEntityLabel";
import styles from "./ObjectRefLabel.module.less";

const EntityLinkLabel: React.FC<{ id: string; title: React.ReactNode }> = ({
  id,
  title,
}) => (
  <a
    href={makeUrl(PageUrl.entityCard, { id })}
    target="_blank"
    onClick={(e) => e.stopPropagation()}
    rel="noreferrer"
    style={{ width: "100%" }}
  >
    {title}
  </a>
);

const EntityTagLabel: React.FC<{
  viewInfo?: ZAttrViewInfo;
  title: React.ReactNode;
}> = ({ viewInfo, title }) => {
  const viewProps = viewInfo?.component;
  if (viewProps?.view !== "ObectRef") return null;
  return (
    <Tag
      color={viewProps?.modeMeta?.tagColor}
      style={{ ...viewInfo?.styles, maxWidth: "100%" }}
      className={styles[viewInfo?.appearance?.view?.overflow || ""]}
    >
      {title}
    </Tag>
  );
};

const createLabel = (
  entityId: string,
  content: React.ReactNode,
  viewInfo?: ZAttrViewInfo,
) => {
  const viewProps = viewInfo?.component;
  if (viewProps?.view !== "ObectRef") return null;
  const lablelMap: Partial<Record<ZModeObjectRef, React.ReactNode>> = {
    link: <EntityLinkLabel id={entityId} title={content} />,
    tag: <EntityTagLabel title={content} viewInfo={viewInfo} />,
    standart: (
      <SimpleText
        values={[content]}
        viewInfo={viewInfo}
        className={styles.label}
      />
    ),
  };
  const mode = viewProps?.mode;
  return mode ? lablelMap[mode] : lablelMap.standart;
};

// Сущность из одиночных значений выводится в строку вместе с разделителем.
// Сущность с многозначным/вложенным полем или изображением, как и раньше,
// занимает всю ширину (LPLM-3670).
const isInlineEntity = (
  keys: string[],
  attrTreeData: ZAttrLabelTreeData,
  attributeList: ZAttribute[],
  typesMap: Record<number, string>,
  labelAttrs: ZUid[],
) =>
  keys
    .map((key) => findNodeByKey(key, [attrTreeData]))
    .filter((node): node is ZAttrLabelTreeData =>
      labelAttrs.some((id) => String(id) === String(node?.attrId)),
    )
    .every((node) => {
      const attribute = attributeList.find(({ id }) => id === node.attrId);
      return (
        !node.children?.length &&
        (node.labels?.length ?? 0) <= 1 &&
        (!attribute || typesMap[attribute.valueType] !== AttrTypeName.image)
      );
    });

interface PropsObjectRefLabel {
  params: MakeLabelParams;
}

export const ObjectRefLabel: React.FC<PropsObjectRefLabel> = ({ params }) => {
  const [label, setLabel] = useState<React.ReactNode>(null);
  useEffect(() => {
    try {
      const { attribute, attributeList, typesMap } = params;
      const viewInfo = getViewInfo(attribute.viewType);
      if (viewInfo?.component?.view !== "ObectRef") throw Error("system error");
      const attrTreeData = getAttrLabelTreeData(
        params.attribute,
        params.labelTreeData,
      );
      if (!attrTreeData) return;
      const meta = zAttrLabelMeta.parse(attrTreeData.attrLabelMeta);
      if (meta.type !== AttrTypeName.object) {
        throw Error("system error");
      }

      const labelAttrs = viewInfo.component.labelAtts || [];

      const eIds = Object.keys(meta.links);
      const eLabels: [string, React.ReactNode][] = eIds.map((id) => [
        id,
        getEntityLabel(
          id,
          attrTreeData,
          attributeList,
          typesMap,
          labelAttrs,
          (separator) => <span className={styles.separator}>{separator}</span>,
        ),
      ]);
      const multiple = eLabels.length > 1;
      const separator = viewInfo.appearance?.view?.separator;
      const newLine = separator === "newLine";
      // Разделитель находится внутри блока сущности, поэтому не переносится
      // на отдельную строку; при "новой строке" каждая сущность — свой блок.
      setLabel(
        <div className={classNames([[multiple, styles.multiple]])}>
          {eLabels.map(([id, entityLabel], index) => (
            <div
              key={id}
              className={classNames([
                [
                  !newLine,
                  isInlineEntity(
                    meta.links[id] ?? [],
                    attrTreeData,
                    attributeList,
                    typesMap,
                    labelAttrs,
                  )
                    ? styles.item
                    : styles.wide,
                ],
              ])}
            >
              {createLabel(id, entityLabel, viewInfo)}
              {!newLine && index < eLabels.length - 1 && (
                <span className={styles.separator}>
                  {getSeparator(separator)}
                </span>
              )}
            </div>
          ))}
        </div>,
      );
    } catch (error) {
      onError(error);
    }
  }, []);

  return label;
};
